import { CarvanaTheme as theme } from '@carvana/theme';
import { isServerSideRendering } from '../../utilities';

const defaultAction = { type: '' };

type ScreenSize = {
  height: number;
  isDesktop: boolean;
  isLandscape: boolean;
  isMobile: boolean;
  isPortrait: boolean;
  isTablet: boolean;
  width: number;
  widthLg: boolean;
  widthMd: boolean;
  widthSm: boolean;
  widthXl: boolean;
  widthXs: boolean;
};

export type UiState = {
  screenSize: ScreenSize;
};

const SCREEN_RESIZE = '@@ui/SCREEN_RESIZE';
const SCREEN_ORIENTATION = '@@ui/SCREEN_ORIENTATION';

function screenResize(payload: { height: number; width: number }) {
  return {
    type: SCREEN_RESIZE,
    payload
  };
}

function screenOrientation() {
  return {
    type: SCREEN_ORIENTATION
  };
}

export function initResizeListener(store: { dispatch: (action: any) => void }) {
  const DEFAULT_HEIGHT = 1000;
  const DEFAULT_WIDTH = theme.breakpoints.values.lg;

  store.dispatch(
    screenResize({
      height: isServerSideRendering ? DEFAULT_HEIGHT : window.innerHeight,
      width: isServerSideRendering ? DEFAULT_WIDTH : window.innerWidth
    })
  );

  if (isServerSideRendering) {
    return;
  }

  let pendingUpdate = false;
  window.addEventListener('resize', () => {
    if (pendingUpdate) return;
    pendingUpdate = true;
    window.requestAnimationFrame(() => {
      pendingUpdate = false;

      if (!window.visualViewport) return;

      const { height, width } = window.visualViewport;

      store.dispatch(screenResize({ height, width }));
    });
  });

  let orientationTimeout: number;
  window.addEventListener(
    'orientationchange',
    () => {
      if (orientationTimeout) {
        window.cancelAnimationFrame(orientationTimeout);
      }

      orientationTimeout = window.requestAnimationFrame(() => {
        store.dispatch(screenOrientation());
      });
    },
    false
  );
}

function getSizes({ height, width }: { height: number; width: number }) {
  const widthXs = width < theme.breakpoints.values.sm;
  const widthSm = width >= theme.breakpoints.values.sm && width < theme.breakpoints.values.md;
  const widthMd = width >= theme.breakpoints.values.md && width < theme.breakpoints.values.lg;
  const widthLg = width >= theme.breakpoints.values.lg && width < theme.breakpoints.values.xl;
  const widthXl = width >= theme.breakpoints.values.xl;
  const isPortrait = isServerSideRendering ? false : window.innerHeight > window.innerWidth;
  const isLandscape = isServerSideRendering ? true : window.innerHeight < window.innerWidth;

  return {
    screenSize: {
      width,
      height,
      widthXs,
      widthSm,
      widthMd,
      widthLg,
      widthXl,
      isPortrait,
      isLandscape,
      isMobile: widthXs,
      isTablet: widthSm,
      isDesktop: widthMd || widthLg || widthXl
    }
  };
}

type ScreenAction = { type: string; payload?: { height: number; width: number } };

export default function ui(state: Partial<UiState> = {}, action: ScreenAction = defaultAction) {
  const { type, payload } = action;

  switch (type) {
    case SCREEN_RESIZE: {
      return {
        ...state,
        ...getSizes(payload!)
      };
    }

    default:
      return state;
  }
}
